<template>
  <div class="oss-batch-upload-page">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/menu/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>系统服务</el-breadcrumb-item>
      <el-breadcrumb-item>OSS 批量上传</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="oss-batch-upload-page__card">
      <div slot="header" class="oss-batch-upload-page__section-header">
        <div>
          <div class="oss-batch-upload-page__title">阿里云 OSS 批量上传</div>
          <div class="oss-batch-upload-page__subtitle">支持文件批量上传，自动汇总 URL；视频文件会额外生成封面，并展示时长（秒、毫秒，整数）。</div>
        </div>
        <el-tag type="success">共 {{ uploadResults.length }} 个文件</el-tag>
      </div>

      <el-form :inline="true" label-width="92px">
        <el-form-item label="上传目录">
          <el-input
            v-model.trim="uploadDir"
            class="oss-batch-upload-page__dir-input"
            placeholder="例如：cms/manual_upload"
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button :disabled="uploading" type="primary" @click="triggerSelectFiles">选择文件</el-button>
          <el-button :disabled="!pendingFiles.length || uploading" type="success" @click="startUpload">开始上传</el-button>
          <el-button :disabled="uploading" @click="clearPendingFiles">清空待上传</el-button>
          <el-button :disabled="!pendingFiles.length && !uploadResults.length" type="warning" plain @click="exportExcel">导出 Excel</el-button>
        </el-form-item>
      </el-form>

      <input
        ref="fileInput"
        class="oss-batch-upload-page__file-input"
        multiple
        type="file"
        @change="handleFileSelect"
      />

      <div class="oss-batch-upload-page__tip">
        <i class="el-icon-info"></i>
        <span>说明：文件会上传到你填写的 OSS 目录；视频封面使用 OSS 截帧参数实时生成，不再单独上传封面文件。</span>
      </div>

      <el-table
        :data="pendingFiles"
        border
        class="oss-batch-upload-page__table"
        empty-text="请先选择要上传的文件"
        stripe
      >
        <el-table-column label="序号" type="index" width="70"></el-table-column>
        <el-table-column label="文件名" min-width="280" prop="name"></el-table-column>
        <el-table-column label="类型" min-width="120">
          <template slot-scope="scope">{{ getFileTypeLabel(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="大小" min-width="120">
          <template slot-scope="scope">{{ formatFileSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column label="时长（秒）" min-width="140">
          <template slot-scope="scope">{{ formatDurationSeconds(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="时长（毫秒）" min-width="150">
          <template slot-scope="scope">{{ formatDurationMilliseconds(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="removePendingFile(scope.$index)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card class="oss-batch-upload-page__card">
      <div slot="header" class="oss-batch-upload-page__section-header">
        <span>上传结果</span>
        <div class="oss-batch-upload-page__section-actions">
          <el-progress
            v-if="uploading"
            :percentage="overallProgress"
            :stroke-width="16"
            class="oss-batch-upload-page__progress"
          ></el-progress>
          <el-button :disabled="!uploadResults.length" size="mini" @click="copyUrls">复制 URL 列表</el-button>
        </div>
      </div>

      <el-table
        :data="uploadResults"
        border
        class="oss-batch-upload-page__table"
        empty-text="上传完成后会在这里展示结果"
        stripe
      >
        <el-table-column label="序号" type="index" width="70"></el-table-column>
        <el-table-column label="文件名" min-width="220" prop="fileName"></el-table-column>
        <el-table-column label="文件类型" min-width="110" prop="fileType"></el-table-column>
        <el-table-column label="文件大小" min-width="120">
          <template slot-scope="scope">{{ formatFileSize(scope.row.fileSize) }}</template>
        </el-table-column>
        <el-table-column label="时长（秒）" min-width="140" prop="durationSecondsDisplay"></el-table-column>
        <el-table-column label="时长（毫秒）" min-width="150" prop="durationMillisecondsDisplay"></el-table-column>
        <el-table-column label="文件 URL" min-width="320">
          <template slot-scope="scope">
            <a :href="scope.row.fileUrl" class="oss-batch-upload-page__link" target="_blank">{{ scope.row.fileUrl }}</a>
          </template>
        </el-table-column>
        <el-table-column label="封面 URL" min-width="320">
          <template slot-scope="scope">
            <span v-if="scope.row.coverUrl">
              <a :href="scope.row.coverUrl" class="oss-batch-upload-page__link" target="_blank">{{ scope.row.coverUrl }}</a>
            </span>
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="上传时间" min-width="170" prop="uploadTime"></el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import { uploadFile } from '@/utils/AliOssUploadUtils'

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'wmv', 'webm', 'mkv']

function normalizeDir (dir) {
  return (dir || '').replace(/^\/+|\/+$/g, '')
}

export default {
  name: 'OssBatchUploadPage',
  data () {
    return {
      uploadDir: 'cms/manual_upload',
      pendingFiles: [],
      uploadResults: [],
      uploading: false,
      overallProgress: 0
    }
  },
  methods: {
    triggerSelectFiles () {
      this.$refs.fileInput.click()
    },
    async handleFileSelect (event) {
      const files = Array.from(event.target.files || [])
      if (!files.length) return
      const fileItems = await Promise.all(files.map(file => this.createPendingFileItem(file)))
      this.pendingFiles = this.pendingFiles.concat(fileItems)
      event.target.value = ''
    },
    async createPendingFileItem (file) {
      const fileItem = {
        uid: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        durationSeconds: null,
        durationMilliseconds: null,
        durationError: false
      }

      if (!this.isVideoFile(file)) {
        return fileItem
      }

      try {
        const durationSeconds = await this.getVideoDurationSeconds(file)
        fileItem.durationSeconds = durationSeconds
        fileItem.durationMilliseconds = Math.round(durationSeconds * 1000)
      } catch (error) {
        fileItem.durationError = true
      }

      return fileItem
    },
    getVideoDurationSeconds (file) {
      return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file)
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        const clear = () => {
          video.removeAttribute('src')
          video.load()
          URL.revokeObjectURL(objectUrl)
        }
        video.onloadedmetadata = () => {
          const duration = Number(video.duration)
          clear()
          if (Number.isFinite(duration) && duration >= 0) {
            resolve(duration)
            return
          }
          reject(new Error('invalid duration'))
        }
        video.onerror = () => {
          clear()
          reject(new Error('read duration failed'))
        }
        video.src = objectUrl
      })
    },
    removePendingFile (index) {
      this.pendingFiles.splice(index, 1)
    },
    clearPendingFiles () {
      this.pendingFiles = []
    },
    async startUpload () {
      const dir = normalizeDir(this.uploadDir)
      if (!dir) {
        return this.$message.warning('请先填写上传目录')
      }
      if (!this.pendingFiles.length) {
        return this.$message.warning('请先选择文件')
      }

      this.uploading = true
      this.overallProgress = 0
      const total = this.pendingFiles.length
      const finishedResults = []

      try {
        for (let index = 0; index < total; index++) {
          const fileItem = this.pendingFiles[index]
          const fileUrl = await this.uploadSingleFile(dir, fileItem.file, percent => {
            const currentProgress = (index + percent / 100) / total
            this.overallProgress = Math.min(100, Math.max(0, Math.round(currentProgress * 100)))
          })

          let coverUrl = ''
          if (this.isVideoFile(fileItem)) {
            coverUrl = this.getVideoCoverUrl(fileUrl)
          }

          finishedResults.push({
            fileName: fileItem.name,
            fileType: this.getFileTypeLabel(fileItem),
            fileSize: fileItem.size,
            durationSeconds: fileItem.durationSeconds,
            durationMilliseconds: fileItem.durationMilliseconds,
            durationSecondsDisplay: this.formatDurationSeconds(fileItem),
            durationMillisecondsDisplay: this.formatDurationMilliseconds(fileItem),
            fileUrl,
            coverUrl,
            uploadTime: this.formatDateTime(new Date())
          })

          this.overallProgress = Math.round(((index + 1) / total) * 100)
        }

        this.uploadResults = finishedResults.concat(this.uploadResults)
        this.pendingFiles = []
        this.$message.success(`成功上传 ${finishedResults.length} 个文件`)
      } catch (error) {
        const message = error?.message || '上传失败'
        this.$message.error(message)
      } finally {
        this.uploading = false
      }
    },
    uploadSingleFile (dir, file, onProgress) {
      return new Promise((resolve, reject) => {
        uploadFile(
          dir,
          file,
          percent => {
            if (onProgress) onProgress(percent)
          },
          fileUrl => {
            resolve(fileUrl)
          },
          error => {
            reject(new Error(typeof error === 'string' ? error : '上传失败'))
          }
        )
      })
    },
    isVideoFile (file) {
      const fileType = file.type || file.file?.type || ''
      const fileName = file.name || file.file?.name || ''
      if (fileType && fileType.startsWith('video/')) return true
      const ext = (fileName.split('.').pop() || '').toLowerCase()
      return VIDEO_EXTENSIONS.includes(ext)
    },
    getVideoCoverUrl (fileUrl) {
      if (!fileUrl) return ''
      const separator = fileUrl.includes('?') ? '&' : '?'
      return `${fileUrl}${separator}x-oss-process=video/snapshot,t_1000,f_jpg,w_0,h_0,m_fast`
    },
    exportExcel () {
      if (!this.pendingFiles.length && !this.uploadResults.length) {
        return this.$message.warning('暂无可导出的数据')
      }
      const rows = this.pendingFiles.map((item, index) => ({
        序号: index + 1,
        文件名: item.name,
        文件类型: this.getFileTypeLabel(item),
        文件大小: this.formatFileSize(item.size),
        时长秒: this.formatDurationSeconds(item),
        时长毫秒: this.formatDurationMilliseconds(item),
        文件URL: '',
        封面URL: '',
        上传时间: ''
      }))
      const resultRows = this.uploadResults.map((item, index) => ({
        序号: this.pendingFiles.length + index + 1,
        文件名: item.fileName,
        文件类型: item.fileType,
        文件大小: this.formatFileSize(item.fileSize),
        时长秒: item.durationSecondsDisplay,
        时长毫秒: item.durationMillisecondsDisplay,
        文件URL: item.fileUrl,
        封面URL: item.coverUrl || '',
        上传时间: item.uploadTime
      }))
      const worksheet = XLSX.utils.json_to_sheet(rows.concat(resultRows))
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'OSS上传结果')
      XLSX.writeFile(workbook, `oss-upload-${Date.now()}.xlsx`)
      this.$message.success('Excel 已导出')
    },
    copyUrls () {
      if (!this.uploadResults.length) {
        return this.$message.warning('暂无可复制的数据')
      }
      const text = this.uploadResults.map(item => item.fileUrl).join('\n')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('URL 列表已复制')
        }).catch(() => {
          this.fallbackCopy(text)
        })
        return
      }
      this.fallbackCopy(text)
    },
    fallbackCopy (text) {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      this.$message.success('URL 列表已复制')
    },
    formatFileSize (size) {
      if (!size && size !== 0) return '--'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
      if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`
      return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`
    },
    getFileTypeLabel (file) {
      if (this.isVideoFile(file)) return '视频'
      const fileType = file.type || file.file?.type || ''
      if (fileType && fileType.startsWith('image/')) return '图片'
      return '普通文件'
    },
    formatDurationSeconds (file) {
      if (!this.isVideoFile(file)) return '--'
      if (file.durationError) return '获取失败'
      if (file.durationSeconds === null || file.durationSeconds === undefined) return '--'
      return String(Math.round(file.durationSeconds))
    },
    formatDurationMilliseconds (file) {
      if (!this.isVideoFile(file)) return '--'
      if (file.durationError) return '获取失败'
      if (file.durationMilliseconds === null || file.durationMilliseconds === undefined) return '--'
      return String(file.durationMilliseconds)
    },
    formatDateTime (value) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return '--'
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    }
  }
}
</script>

<style scoped>
.oss-batch-upload-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.oss-batch-upload-page__card {
  margin-top: 18px;
}

.oss-batch-upload-page__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.oss-batch-upload-page__subtitle {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
}

.oss-batch-upload-page__section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.oss-batch-upload-page__section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.oss-batch-upload-page__dir-input {
  width: 320px;
}

.oss-batch-upload-page__file-input {
  display: none;
}

.oss-batch-upload-page__tip {
  margin-bottom: 18px;
  padding: 10px 14px;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  background: #f4faff;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 8px;
}

.oss-batch-upload-page__table {
  width: 100%;
}

.oss-batch-upload-page__progress {
  width: 220px;
}

.oss-batch-upload-page__link {
  color: #409eff;
  word-break: break-all;
}
</style>
